import type { LiveBreak, LiveBreakEntry } from "@loot-os/api-contract";

/**
 * What the live-break views read off a live break, derived from the wire's fields alone (no
 * clock): `status` is only `open` or `closed`, and everything else is the fields that say it.
 */

/** A live break's badge: LIVE, ENDING (Draining) or CLOSED; a queue row's LIVE and NEXT. */
export type LiveBreakBadgeStatus = "live" | "next" | "ending" | "closed";

/** Whether a live break takes purchases now: `stopped` covers Purchases stopped, Draining and a host away. */
export type LiveBreakIntake = "open" | "stopped" | "closed";

/** The badge a listing shows: closed, else ending while it Drains, else live. */
export function liveBreakListingStatus(
  liveBreak: Pick<LiveBreak, "status" | "isDraining">,
): Exclude<LiveBreakBadgeStatus, "next"> {
  if (liveBreak.status === "closed") return "closed";
  return liveBreak.isDraining ? "ending" : "live";
}

/** Whether a purchase made now is taken: closed, refused for a reason, or open. */
export function liveBreakIntake(
  liveBreak: Pick<LiveBreak, "status" | "purchaseRefusal" | "isAcceptingPurchases" | "isDraining">,
): LiveBreakIntake {
  if (liveBreak.status === "closed") return "closed";
  const refused =
    liveBreak.purchaseRefusal !== null || !liveBreak.isAcceptingPurchases || liveBreak.isDraining;
  return refused ? "stopped" : "open";
}

/**
 * Entries still waiting, the one on stage left out: the server's `queuedCount` keeps a presented
 * entry until its Reveal opens it (boxed's `getPendingQueuedCount`).
 */
export function pendingQueuedCount(liveBreak: Pick<LiveBreak, "queuedCount" | "revealPhase">) {
  const presenting = liveBreak.revealPhase === "presenting" ? 1 : 0;
  return Math.max(liveBreak.queuedCount - presenting, 0);
}

/** The name a live break's host goes by: theirs, or the store's on a house live break. */
export function liveBreakHostName(liveBreak: Pick<LiveBreak, "host">, houseName: string): string {
  return liveBreak.host?.displayName ?? houseName;
}

/** The Twitch channel's name to show: Twitch's display name for it, else the login; null with no channel. */
export function liveBreakChannelName(
  liveBreak: Pick<LiveBreak, "twitchChannel" | "twitchStatus">,
): string | null {
  if (!liveBreak.twitchChannel) return null;
  return liveBreak.twitchStatus?.displayName || liveBreak.twitchChannel;
}

/**
 * A wait in whole minutes, as the queue dialogs show it: rounded up, so a wait under a minute
 * reads as one and an estimate never promises sooner than the server's. Null stays null.
 */
export function waitMinutes(waitMs: number | null | undefined): number | null {
  if (waitMs === null || waitMs === undefined) return null;
  return Math.max(1, Math.ceil(waitMs / 60_000));
}

/** What a set of entries holds from the balance while queued: Σ `costCents`. */
export function heldCents(entries: readonly { costCents: number }[]): number {
  return entries.reduce((sum, entry) => sum + entry.costCents, 0);
}

/** A queue row's prominence: the entry on stage, the one Up Next, or one waiting. */
export type LiveBreakQueueRowStatus = "live" | "next" | "queued";

/**
 * Where an entry of the queue read stands: `live` while it is the one presenting (it has no
 * place), `next` at place 1, else `queued`.
 */
export function queueRowStatus(
  entry: Pick<LiveBreakEntry, "id" | "position">,
  presentingEntryId: string | null,
): LiveBreakQueueRowStatus {
  if (entry.id === presentingEntryId) return "live";
  return entry.position === 1 ? "next" : "queued";
}
